import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, LogOut, ArrowRight, ShieldCheck, Activity, Building2, Plus, Copy, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { AuthLayout } from './LoginPage';
import { EmptyState, QueryError, Status } from '@/workspace/ui';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const KINDS = ['all', 'subscription', 'login', 'settings', 'onboarding', 'domain', 'api_key', 'team', 'automation', 'admin', 'tenant'];

function timeAgo(ts) {
  if (!ts) return '';
  const s = Math.max(0, (Date.now() - new Date(ts).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 7) return `${Math.floor(s / 86400)}d ago`;
  return new Date(ts).toLocaleDateString();
}

/* Operator view after admin login: pick a workspace, or audit the activity feed. */
export default function AdminTenantPicker({ adminToken, onPick, onExit }) {
  const [view, setView] = useState('workspaces');
  const [tenants, setTenants] = useState(null);
  const [tenantError, setTenantError] = useState('');
  const [tenantRetry, setTenantRetry] = useState(0);
  const [busy, setBusy] = useState('');
  const [pickError, setPickError] = useState('');
  const [events, setEvents] = useState(null);
  const [activityError, setActivityError] = useState('');
  const [activityRetry, setActivityRetry] = useState(0);
  const [activityLoading, setActivityLoading] = useState(false);
  const [tenantFilter, setTenantFilter] = useState('');
  const [kindFilter, setKindFilter] = useState('all');
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState('');
  const mutating = useRef(false);
  const [createdLink, setCreatedLink] = useState(null);
  const [createdNotice, setCreatedNotice] = useState('');
  const [copyStatus, setCopyStatus] = useState('idle');
  const copying = useRef(false);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${adminToken}` }), [adminToken]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setTenants(null);
    setTenantError('');
    fetch(`${BACKEND_URL}/api/admin/tenants`, { headers: authHeaders, signal: controller.signal })
      .then(r => { if (!r.ok) throw new Error('Could not load workspaces. Try again.'); return r.json(); })
      .then(rows => {
        if (!Array.isArray(rows)) throw new Error('Could not load workspaces. The response was invalid.');
        if (active) setTenants(rows);
      })
      .catch(() => { if (active) setTenantError('Could not load workspaces. Try again.'); });
    return () => { active = false; controller.abort(); };
  }, [authHeaders, tenantRetry]);

  // Keep failed loads distinct from empty results, and never overlap polling.
  useEffect(() => {
    if (view !== 'activity') return undefined;
    let dead = false;
    let inFlight = false;
    const controller = new AbortController();
    const load = async () => {
      if (dead || inFlight) return;
      inFlight = true;
      setActivityLoading(true);
      const params = new URLSearchParams({ limit: '150' });
      if (tenantFilter) params.set('tenant_id', tenantFilter);
      if (kindFilter !== 'all') params.set('kind', kindFilter);
      try {
        const res = await fetch(`${BACKEND_URL}/api/admin/activity?${params}`, { headers: authHeaders, signal: controller.signal });
        if (!res.ok) throw new Error('Could not load activity. Try again.');
        const rows = await res.json();
        if (!Array.isArray(rows)) throw new Error('Could not load activity. The response was invalid.');
        if (!dead) { setEvents(rows); setActivityError(''); }
      } catch {
        if (!dead) setActivityError('Could not load activity. Any displayed events may be out of date.');
      } finally {
        inFlight = false;
        if (!dead) setActivityLoading(false);
      }
    };
    setEvents(null);
    setActivityError('');
    load();
    const timer = setInterval(load, 12000);
    return () => { dead = true; controller.abort(); clearInterval(timer); };
  }, [view, tenantFilter, kindFilter, authHeaders, activityRetry]);

  const impersonate = async (t) => {
    if (mutating.current) return;
    mutating.current = true;
    setBusy(t.id);
    setPickError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/tenants/${t.id}/impersonate`, {
        method: 'POST', headers: authHeaders,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Impersonation failed');
      await onPick(data);
      toast.success(`Logged into ${t.name}`);
    } catch (e) {
      setPickError(e.message || 'Could not open this workspace. Try again.');
      toast.error(e.message);
    } finally {
      mutating.current = false;
      setBusy('');
    }
  };

  const createWorkspace = async e => {
    e.preventDefault();
    if (mutating.current) return;
    const name = newName.trim();
    const email = newEmail.trim().toLowerCase();
    if (!name) { setCreateError('Enter a company name'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setCreateError('Enter a valid owner email'); return; }
    mutating.current = true;
    setCreateBusy(true);
    setCreateError('');
    setCreatedLink(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/tenants`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, send_welcome: true }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Create failed');
      setTenants(prev => [...(prev || []), data]);
      setNewName(''); setNewEmail('');
      if (data.email_sent) {
        setCreatedNotice(`${name} created. Welcome email sent to ${email}.`);
        toast.success(`${name} created — welcome email sent to ${email}`);
        setCreating(false);
      } else {
        const message = data.setup_link
          ? `${name} created, but the welcome email was not sent. Share the setup link below with the owner.`
          : `${name} created, but the welcome email was not sent and no setup link was returned. Open the workspace and review Settings → Team before inviting the owner.`;
        setCreatedNotice(message);
        toast.info(message);
        setCreatedLink(data.setup_link || null);
      }
    } catch (e) {
      setCreateError(e.message || 'Could not create this workspace. Try again.');
      toast.error(e.message);
    } finally {
      mutating.current = false;
      setCreateBusy(false);
    }
  };

  const copySetupLink = async () => {
    if (copying.current || !createdLink) return;
    copying.current = true;
    setCopyStatus('copying');
    try {
      await navigator.clipboard.writeText(createdLink);
      setCopyStatus('copied');
      toast.success('Link copied');
    } catch {
      setCopyStatus('error');
      toast.error('Could not copy the setup link. Select it and copy it manually.');
    } finally {
      copying.current = false;
    }
  };

  return (
    <AuthLayout wide eyebrow="Operator workspace">
      <div className="sp-auth-operator-heading"><h2>Workspace access.</h2><Status><ShieldCheck size={13} aria-hidden="true" /> Operator</Status></div>
      <p>Manage workspace access and review account activity.</p>
      <nav className="sp-auth-tabs" aria-label="Operator views">
        {[['workspaces', 'Workspaces', Building2], ['activity', 'Activity', Activity]].map(([id, label, Icon]) => (
          <button type="button" key={id} data-testid={`admin-view-${id}`} disabled={createBusy || !!busy} onClick={() => setView(id)} aria-pressed={view === id} aria-controls={`admin-${id}`}><Icon size={15} aria-hidden="true" />{label}</button>
        ))}
      </nav>
      {view === 'workspaces' ? <section id="admin-workspaces" aria-labelledby="admin-workspaces-title">
        {pickError && <div className="sp-auth-notice" data-tone="error" role="alert">{pickError}</div>}
        {createdNotice && <div className="sp-auth-notice" data-tone={createdLink ? undefined : 'success'} role="status">{createdNotice}</div>}
        <div className="sp-auth-section-head"><div><h3 id="admin-workspaces-title">Choose a workspace</h3><p>Open the dashboard as that workspace sees it.</p></div>
          <button type="button" data-testid="admin-new-workspace" className="sp-primary-button" disabled={createBusy || !!busy} aria-expanded={creating} aria-controls="admin-create-form" onClick={() => { setCreating(v => !v); setCreateError(''); }}><Plus size={15} aria-hidden="true" />{creating ? 'Close form' : 'New workspace'}</button>
        </div>
        {creating && <form id="admin-create-form" className="sp-auth-create" onSubmit={createWorkspace} aria-busy={createBusy} aria-describedby={createError ? 'admin-create-error' : undefined} noValidate>
          <div className="sp-auth-create-fields">
            <div className="sp-auth-field"><label htmlFor="admin-company-name">Company name</label><input id="admin-company-name" name="company" data-testid="admin-create-name" value={newName} onChange={e => setNewName(e.target.value)} placeholder="Acme Coaching" autoComplete="organization" required maxLength={120} disabled={createBusy || !!busy} /></div>
            <div className="sp-auth-field"><label htmlFor="admin-owner-email">Owner email</label><input id="admin-owner-email" name="email" type="email" data-testid="admin-create-email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="owner@company.com" autoComplete="email" required disabled={createBusy || !!busy} /></div>
          </div>
          <p>The owner receives a password setup link, then chooses their funnel and tracking domain.</p>
          {createError && <div id="admin-create-error" className="sp-auth-notice" data-tone="error" role="alert">{createError}</div>}
          <div className="sp-auth-actions sp-auth-actions-end"><button type="submit" data-testid="admin-create-submit" className="sp-primary-button" disabled={createBusy || !!busy}>{createBusy && <Loader2 size={15} className="sp-spin" aria-hidden="true" />}{createBusy ? 'Creating…' : 'Create workspace'}</button></div>
          {createdLink && <div className="sp-auth-code"><Link2 size={15} aria-hidden="true" /><code>{createdLink}</code><button type="button" className="sp-secondary-button" aria-label="Copy setup link" disabled={copyStatus === 'copying'} aria-busy={copyStatus === 'copying'} onClick={copySetupLink}><Copy size={15} aria-hidden="true" /><span aria-live="polite">{copyStatus === 'copying' ? 'Copying…' : copyStatus === 'copied' ? 'Copied' : 'Copy'}</span></button></div>}
          {copyStatus === 'error' && <div className="sp-auth-notice" data-tone="error" role="alert">Could not copy the setup link. Select it and copy it manually.</div>}
        </form>}
        {tenantError ? <QueryError message={tenantError} onRetry={() => setTenantRetry(v => v + 1)} /> : tenants === null ? <div className="sp-auth-loading" role="status"><Loader2 size={18} className="sp-spin" aria-hidden="true" />Loading workspaces…</div>
          : tenants.length === 0 ? <EmptyState icon={Building2} title="No workspaces yet." description="Create a workspace to give its owner access to Tether." />
            : <ul className="sp-auth-tenant-list" aria-label="Workspaces">{tenants.map(tenant => <li key={tenant.id}>
              <button type="button" data-testid={`admin-pick-${tenant.id}`} aria-label={`Open ${tenant.name} workspace`} onClick={() => impersonate(tenant)} disabled={createBusy || !!busy} aria-busy={busy === tenant.id}>
                <span className="sp-auth-avatar" aria-hidden="true">{(tenant.name || '?').charAt(0).toUpperCase()}</span>
                <span className="sp-auth-tenant-copy"><strong>{tenant.name}</strong><small>{tenant.email}</small></span>
                <span className="sp-auth-open">{busy === tenant.id ? <><Loader2 size={15} className="sp-spin" aria-hidden="true" />Opening…</> : <>Open<ArrowRight size={15} aria-hidden="true" /></>}</span>
              </button>
            </li>)}</ul>}
      </section> : <section id="admin-activity" aria-labelledby="admin-activity-title">
        <div className="sp-auth-section-head"><div><h3 id="admin-activity-title">Activity</h3><p>Workspace activity, refreshed every 12 seconds.</p></div><button type="button" className="sp-secondary-button" onClick={() => setActivityRetry(v => v + 1)} disabled={activityLoading} aria-busy={activityLoading}>{activityLoading ? 'Refreshing…' : 'Refresh activity'}</button></div>
        <div className="sp-auth-filters">
          <div className="sp-auth-field"><label htmlFor="admin-activity-workspace">Workspace</label><select id="admin-activity-workspace" data-testid="activity-tenant-filter" value={tenantFilter} onChange={e => setTenantFilter(e.target.value)}><option value="">All workspaces</option>{(tenants || []).map(tenant => <option key={tenant.id} value={tenant.id}>{tenant.name}</option>)}</select></div>
          <div className="sp-auth-field"><label htmlFor="admin-activity-kind">Event type</label><select id="admin-activity-kind" data-testid="activity-kind-filter" value={kindFilter} onChange={e => setKindFilter(e.target.value)}>{KINDS.map(kind => <option key={kind} value={kind}>{kind === 'all' ? 'All events' : kind.replace('_', ' ')}</option>)}</select></div>
        </div>
        {activityError && <QueryError message={activityError} onRetry={() => setActivityRetry(v => v + 1)} />}
        {events === null ? (!activityError && <div className="sp-auth-loading" role="status"><Loader2 size={18} className="sp-spin" aria-hidden="true" />Loading activity…</div>)
          : events.length === 0 ? (!activityError && <EmptyState icon={Activity} title={tenantFilter || kindFilter !== 'all' ? 'No activity matches these filters.' : 'No activity yet.'} description="Workspace events will appear here." />)
            : <ol className="sp-auth-event-list" data-testid="activity-feed" aria-label="Workspace activity">{events.map(event => <li key={event.id}>
              <Status>{event.kind?.replace('_', ' ') || 'Event'}</Status><div><strong>{event.message}</strong><small>{event.tenant?.name || 'Workspace unavailable'}{event.actor ? ` · ${event.actor}` : ''} · {timeAgo(event.ts)}</small></div>
            </li>)}</ol>}
      </section>}
      <footer className="sp-auth-operator-footer"><button type="button" className="sp-secondary-button" disabled={createBusy || !!busy} onClick={onExit}><LogOut size={15} aria-hidden="true" /> Exit admin</button><span>Operator session</span></footer>
    </AuthLayout>
  );
}
